<template>
    <redesign-base-chart
        ref="containerRef"
        class="w-full h-full"
        :widget="data"
        :option="chartOption"
    />
</template>
<script setup lang="ts">
import RedesignBaseChart from '@/components/RedesignComponents/Charts/RedesignBaseChart.vue'
import { WidgetExtended } from '@/types/dashboard'
import { computed, ref } from 'vue'
import { useElementSize } from '@vueuse/core'
import { colorsForPieChartCallsPerAccColorsLayout } from '@/enum/colors'
import useWidgetDataWithRefreshInterval from '@/composables/useWidgetDataWithRefreshInterval'
import { isAiQuestionsPieChartWidget } from '@/helpers/widgetUtils'

type LoadedData = Record<string, string|number>

const props = defineProps<{
    data: WidgetExtended
    fontSize: number
    labelsColors: string
}>()

const incomingKey = 'Incoming'
const outgoingKey = 'Outgoing'
const containerRef = ref<HTMLElement | null>(null)
    
const { width: containerWidth } = useElementSize(containerRef)
const { data: chartData } = useWidgetDataWithRefreshInterval<LoadedData[], {data: WidgetExtended}, 'data'>(props, 'data')

//Computed
const mappedOptions = computed(() => {
    if (!chartData.value?.length) {
        return []
    }
    return chartData.value?.reduce((acc, item, i) => {
        if (item.y !== 0) {
            let color = item.color
            if (item.name === incomingKey || item.name === outgoingKey) {
                color = colorsForPieChartCallsPerAccColorsLayout[item.name]
            }
            acc.push({
                name: item.name,
                selected: i === 0,
                value: item.y,
                itemStyle: { color: color }
            })
        }
        return acc
    }, [])
})

const chartOption = computed(() => {
    const isAiQuestionsPie = isAiQuestionsPieChartWidget(props.data)
    const legendFormatter = (name: string) => {
        const total = mappedOptions.value.reduce((sum, item) => sum + item.value, 0)
        const item = mappedOptions.value.find(d => d.name === name)
        const percent = total > 0 ? ((item?.value ?? 0) / total * 100).toFixed(1) : '0'

        return `${name}: ${item?.value ?? 0} (${percent}%)`
    }

    return {
        tooltip: {
            trigger: 'item',
            formatter: '{b}: {c} ({d}%)'
        },
        avoidLabelOverlap: false,
        legend: isAiQuestionsPie
            ? {
                type: 'scroll',
                orient: 'vertical',
                left: 0,
                top: 'bottom',
                textStyle: {
                    width: Math.max(Math.floor(containerWidth.value * 0.30) - 30, 50),
                    overflow: 'truncate',
                },
                tooltip: {
                    show: true,
                    formatter: (params: { name: string }) => legendFormatter(params.name),
                },
                formatter: legendFormatter,
            }
            : { show: false },
        series: [
            {
                type: 'pie',
                minAngle: 1,
                padAngle: 1,
                label: {
                    show: true,
                    formatter: '{b}: {d}%',
                    color: props.labelsColors,
                    fontSize: props.fontSize
                },
                radius: '75%',
                ...(isAiQuestionsPie ? { center: ['65%', '50%'] } : {}),
                data: mappedOptions.value,
                selectedMode: true,
                emphasis: {
                    itemStyle: {
                        shadowBlur: 10,
                        shadowOffsetX: 0,
                        shadowColor: 'rgba(0, 0, 0, 0.5)'
                    }
                }
            }
        ]
    }
})

</script>

<style scoped lang="scss">

</style>
